import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {dashboardStateDefaults, DashboardStateModel} from './dashboard.model';
import {
  dashboardStateUniqueIdentifier,
  LoadDashboardStudyDetails,
  LoadDashboardStudyDetailsFail,
  LoadDashboardStudyDetailsSuccess,
  LoadDashboardUser,
  LoadDashboardUserFail,
  LoadDashboardUserSuccess,
  ResetDashboardStudyDetails,
  ResetDashboardUser,
} from './dashboard.actions';
import {DashboardService} from '../../services/dashboard/dashboard.service';
import {PROGRESS_STATUSES} from '../../../shared/symbols';
import {BackendError} from '../../../messages/messages.actions';

@State<DashboardStateModel>({
  name: dashboardStateUniqueIdentifier,
  defaults: dashboardStateDefaults,
})
@Injectable()
export class DashboardState {
  constructor(private dashboardStudyViewerService: DashboardService) {}

  @Selector()
  static loadDashboardUserProgress(state: DashboardStateModel): DashboardStateModel['loadDashboardUserProgress'] {
    return state.loadDashboardUserProgress;
  }

  @Selector()
  static dashboardUser(state: DashboardStateModel): DashboardStateModel['dashboardUser'] {
    return state.dashboardUser;
  }

  @Selector()
  static lastLoadDashboardUserError(state: DashboardStateModel): DashboardStateModel['lastLoadDashboardUserError'] {
    return state.lastLoadDashboardUserError;
  }

  @Selector()
  static loadDashboardStudyDetailsProgress(state: DashboardStateModel): DashboardStateModel['loadDashboardStudyDetailsProgress'] {
    return state.loadDashboardStudyDetailsProgress;
  }

  @Selector()
  static dashboardStudyDetails(state: DashboardStateModel): DashboardStateModel['dashboardStudyDetails'] {
    return state.dashboardStudyDetails;
  }

  @Selector()
  static lastLoadDashboardStudyDetailsError(state: DashboardStateModel): DashboardStateModel['lastLoadDashboardStudyDetailsError'] {
    return state.lastLoadDashboardStudyDetailsError;
  }

  @Action(LoadDashboardUser)
  loadDashboardStudyViewer(ctx: StateContext<DashboardStateModel>, {dashboardUserUid}: LoadDashboardUser): void {
    ctx.patchState({
      loadDashboardUserProgress: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadDashboardUserError: null,
    });

    this.dashboardStudyViewerService.getDashboardUser(dashboardUserUid).subscribe({
      next: dashboardUser => ctx.dispatch(new LoadDashboardUserSuccess(dashboardUser)),
      error: error => ctx.dispatch(new LoadDashboardUserFail(error)),
    });
  }

  @Action(LoadDashboardUserSuccess)
  loadDashboardStudyViewerSuccess(ctx: StateContext<DashboardStateModel>, {dashboardUser}: LoadDashboardUserSuccess): void {
    ctx.patchState({
      loadDashboardUserProgress: PROGRESS_STATUSES.SUCCEED,
      dashboardUser,
    });
  }

  @Action(LoadDashboardUserFail)
  loadDashboardStudyViewerFail(ctx: StateContext<DashboardStateModel>, {error}: LoadDashboardUserFail): void {
    ctx.patchState({
      loadDashboardUserProgress: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadDashboardUserError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(ResetDashboardUser)
  resetDashboardUserData(ctx: StateContext<DashboardStateModel>): void {
    ctx.patchState({
      loadDashboardUserProgress: PROGRESS_STATUSES.NOT_INITIALIZED,
      dashboardUser: null,
      lastLoadDashboardUserError: null,
    });
  }

  @Action(LoadDashboardStudyDetails)
  loadDashboardStudyDetails(ctx: StateContext<DashboardStateModel>, {dashboardStudyId}: LoadDashboardStudyDetails): void {
    ctx.patchState({
      loadDashboardStudyDetailsProgress: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadDashboardStudyDetailsError: null,
    });

    this.dashboardStudyViewerService.getStudyDetails(dashboardStudyId).subscribe({
      next: dashboardStudyDetails => ctx.dispatch(new LoadDashboardStudyDetailsSuccess(dashboardStudyDetails)),
      error: error => ctx.dispatch(new LoadDashboardStudyDetailsFail(error)),
    });
  }

  @Action(LoadDashboardStudyDetailsSuccess)
  loadDashboardStudyDetailsSuccess(
    ctx: StateContext<DashboardStateModel>,
    {dashboardStudyDetails}: LoadDashboardStudyDetailsSuccess
  ): void {
    ctx.patchState({
      loadDashboardStudyDetailsProgress: PROGRESS_STATUSES.SUCCEED,
      dashboardStudyDetails,
    });
  }

  @Action(LoadDashboardStudyDetailsFail)
  loadDashboardStudyDetailsFail(ctx: StateContext<DashboardStateModel>, {error}: LoadDashboardStudyDetailsFail): void {
    ctx.patchState({
      loadDashboardStudyDetailsProgress: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadDashboardStudyDetailsError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(ResetDashboardStudyDetails)
  resetDashboardStudyDetails(ctx: StateContext<DashboardStateModel>): void {
    ctx.patchState({
      loadDashboardStudyDetailsProgress: PROGRESS_STATUSES.NOT_INITIALIZED,
      dashboardStudyDetails: null,
      lastLoadDashboardStudyDetailsError: null,
    });
  }
}
